// Cookie helpers for preview popup position + size, and viewport fail-safe clamping
import { PREVIEW_POSITION_COOKIE_NAME, PREVIEW_SIZE_COOKIE_NAME } from './state.js';

// Fail-safe bounds: the popup can never shrink below MIN nor grow past the
// viewport minus MARGIN. Keeps the window grabbable and fully on-screen even
// after the browser window is shrunk or the user drags to a smaller display.
export const PREVIEW_MIN_W = 360;
export const PREVIEW_MIN_H = 280;
export const PREVIEW_DEFAULT_W = 800;
export const PREVIEW_DEFAULT_H = 600;
export const PREVIEW_VIEWPORT_MARGIN = 12;

/**
 * Clamp a desired popup box to legal bounds given its top-left corner.
 * Upper bound = viewport minus margins (and minus the offset from left/top so
 * it never overflows the right/bottom edge). Lower bound = MIN.
 */
export function clampPreviewSize(width, height, left, top) {
	const marginedLeft = typeof left === 'number' ? left : PREVIEW_VIEWPORT_MARGIN;
	const marginedTop = typeof top === 'number' ? top : PREVIEW_VIEWPORT_MARGIN;
	const maxW = Math.max(PREVIEW_MIN_W, window.innerWidth - marginedLeft - PREVIEW_VIEWPORT_MARGIN);
	const maxH = Math.max(PREVIEW_MIN_H, window.innerHeight - marginedTop - PREVIEW_VIEWPORT_MARGIN);
	return {
		width: Math.round(Math.max(PREVIEW_MIN_W, Math.min(width, maxW))),
		height: Math.round(Math.max(PREVIEW_MIN_H, Math.min(height, maxH)))
	};
}

export function savePreviewSize(width, height) {
	try {
		const size = JSON.stringify({ width: Math.round(width), height: Math.round(height) });
		const expires = new Date();
		expires.setFullYear(expires.getFullYear() + 1);
		document.cookie = `${PREVIEW_SIZE_COOKIE_NAME}=${encodeURIComponent(size)}; expires=${expires.toUTCString()}; path=/`;
	} catch (e) {
		console.warn('[Preview] Failed to save size to cookie:', e);
	}
}

export function loadPreviewSize() {
	try {
		const cookies = document.cookie.split(';');
		for (let cookie of cookies) {
			cookie = cookie.trim();
			if (cookie.startsWith(PREVIEW_SIZE_COOKIE_NAME + '=')) {
				const value = decodeURIComponent(cookie.substring(PREVIEW_SIZE_COOKIE_NAME.length + 1));
				const size = JSON.parse(value);
				if (size && typeof size.width === 'number' && typeof size.height === 'number') {
					return size;
				}
			}
		}
	} catch (e) {
		console.warn('[Preview] Failed to load size from cookie:', e);
	}
	return null;
}

export function savePreviewPosition(left, top) {
	try {
		const position = JSON.stringify({ left, top });
		const expires = new Date();
		expires.setFullYear(expires.getFullYear() + 1);
		document.cookie = `${PREVIEW_POSITION_COOKIE_NAME}=${encodeURIComponent(position)}; expires=${expires.toUTCString()}; path=/`;
	} catch (e) {
		console.warn('[Preview] Failed to save position to cookie:', e);
	}
}

export function loadPreviewPosition() {
	try {
		const cookies = document.cookie.split(';');
		for (let cookie of cookies) {
			cookie = cookie.trim();
			if (cookie.startsWith(PREVIEW_POSITION_COOKIE_NAME + '=')) {
				const value = decodeURIComponent(cookie.substring(PREVIEW_POSITION_COOKIE_NAME.length + 1));
				const position = JSON.parse(value);
				if (position && typeof position.left === 'number' && typeof position.top === 'number') {
					return position;
				}
			}
		}
	} catch (e) {
		console.warn('[Preview] Failed to load position from cookie:', e);
	}
	return null;
}

export function calculatePopupPosition(savedPosition, cursorX, cursorY, popupWidth, popupHeight, icon) {
	let left, top;
	
	if (savedPosition && cursorX !== null && cursorY !== null) {
		const savedLeft = savedPosition.left;
		const savedTop = savedPosition.top;
		const savedRight = savedLeft + popupWidth;
		const savedBottom = savedTop + popupHeight;
		
		const coversCursor = cursorX >= savedLeft && cursorX <= savedRight && 
		                     cursorY >= savedTop && cursorY <= savedBottom;
		
		if (coversCursor) {
			const rightMargin = 20;
			left = window.innerWidth - popupWidth - rightMargin;
			top = (window.innerHeight - popupHeight) / 2;
			if (top < 10) top = 10;
			if (top + popupHeight > window.innerHeight - 10) {
				top = window.innerHeight - popupHeight - 10;
			}
		} else {
			const maxLeft = window.innerWidth - popupWidth;
			const maxTop = window.innerHeight - popupHeight;
			left = Math.max(0, Math.min(savedPosition.left, maxLeft));
			top = Math.max(0, Math.min(savedPosition.top, maxTop));
		}
	} else if (savedPosition) {
		const maxLeft = window.innerWidth - popupWidth;
		const maxTop = window.innerHeight - popupHeight;
		left = Math.max(0, Math.min(savedPosition.left, maxLeft));
		top = Math.max(0, Math.min(savedPosition.top, maxTop));
	} else {
		if (icon && icon.length) {
			const rightMargin = 20;
			left = window.innerWidth - popupWidth - rightMargin;
			top = (window.innerHeight - popupHeight) / 2;
			if (top < 10) top = 10;
			if (top + popupHeight > window.innerHeight - 10) {
				top = window.innerHeight - popupHeight - 10;
			}
		} else {
			left = (window.innerWidth - popupWidth) / 2;
			top = (window.innerHeight - popupHeight) / 2;
		}
	}
	
	return { left, top };
}

